<!--
  Supervisor Injury Report Form
  
  Step-by-step incident reporting with:
  - 6 steps: Employee Details, Job Details, Incident Details, Incident Analysis, Injury Assessment, Sign-off
  - Comprehensive incident documentation with employee and job information
  - Checkbox group for incident cause selection with conditional "other" specification
  - Multiple conditional validation fields based on user selections
  - Medical treatment validation requiring multiple fields when treatment is sought
  - Form validation for safety compliance and incident documentation
  - Signature requirements on final step
-->

<!-- Use the form layout component to handle the wizard UI -->
<app-form-layout
  [title]="formTitle"
  [form]="form"
  [icon]="formIcon"
  [disabled]="submitting"
  [onSubmit]="onSubmit.bind(this)"
  [steps]="steps"
  [currentStep]="currentStep()"
  [completedSteps]="completedSteps()"
  [canNavigateToStep]="canNavigateToStep.bind(this)"
  [isStepCompleted]="isStepCompleted.bind(this)"
  [onNextStep]="onNextStep.bind(this)"
  [onPreviousStep]="onPreviousStep.bind(this)"
  [getProgressPercentage]="getProgressPercentage.bind(this)"
>
  <div
    *ngIf="form && fieldMappings"
    formContent
    [formGroup]="form"
    class="form-content"
  >
    <!-- Employee & Job Details Step -->
    <div *ngIf="currentStep() === steps[0].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[0].title }}</h1>
            <p>{{ steps[0].description }}</p>
          </ion-label>
        </ion-item-divider>


        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('employeeFirstName')!"
                [label]="fieldMappings['employeeFirstName']"
                [requiredField]="requiredFields['employeeFirstName']"
                placeholder="Enter first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('employeeLastName')!"
                [label]="fieldMappings['employeeLastName']"
                [requiredField]="requiredFields['employeeLastName']"
                placeholder="Enter last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('streetAddress')!"
                [label]="fieldMappings['streetAddress']"
                [requiredField]="requiredFields['streetAddress']"
                placeholder="Enter street address"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('city')!"
                [label]="fieldMappings['city']"
                [requiredField]="requiredFields['city']"
                placeholder="Enter city"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('stateProvince')!"
                [label]="fieldMappings['stateProvince']"
                [requiredField]="requiredFields['stateProvince']"
                placeholder="Enter state/province"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="4">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('zipPostalCode')!"
                [label]="fieldMappings['zipPostalCode']"
                [requiredField]="requiredFields['zipPostalCode']"
                placeholder="Enter ZIP/postal code"
                [type]="'number'"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('phone')!"
                [label]="fieldMappings['phone']"
                [requiredField]="requiredFields['phone']"
                [type]="'tel'"
                placeholder="Enter phone number"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('dateOfHire')!"
                [label]="fieldMappings['dateOfHire']"
                [requiredField]="requiredFields['dateOfHire']"
              
        
              >
              </app-date-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="employeeDetailsFormGroup"
                [control]="employeeDetailsFormGroup.get('craftAndPosition')!"
                [label]="fieldMappings['craftAndPosition']"
                [requiredField]="requiredFields['craftAndPosition']"
                placeholder="Enter craft and position"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Job Details Step -->
    <div *ngIf="currentStep() === steps[1].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[1].title }}</h1>
            <p>{{ steps[1].description }}</p>
          </ion-label>
        </ion-item-divider>


        <ion-grid>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('jobName')!"
                [label]="fieldMappings['jobName']"
                [requiredField]="requiredFields['jobName']"
                placeholder="Enter job name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('shiftWorked')!"
                [label]="fieldMappings['shiftWorked']"
                [requiredField]="requiredFields['shiftWorked']"
                placeholder="Enter shift worked"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('hoursWorkedPerDay')!"
                [label]="fieldMappings['hoursWorkedPerDay']"
                [requiredField]="requiredFields['hoursWorkedPerDay']"
                [type]="'number'"
                placeholder="Enter hours worked per day"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('hoursWorkedPerWeek')!"
                [label]="fieldMappings['hoursWorkedPerWeek']"
                [requiredField]="requiredFields['hoursWorkedPerWeek']"
                [type]="'number'"
                placeholder="Enter hours worked per week"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('supervisorFirstName')!"
                [label]="fieldMappings['supervisorFirstName']"
                [requiredField]="requiredFields['supervisorFirstName']"
                placeholder="Enter supervisor first name"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="jobDetailsFormGroup"
                [control]="jobDetailsFormGroup.get('supervisorLastName')!"
                [label]="fieldMappings['supervisorLastName']"
                [requiredField]="requiredFields['supervisorLastName']"
                placeholder="Enter supervisor last name"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
    
        </ion-grid>
      </ion-list>
    </div>

    <!-- Incident Details Step -->
    <div *ngIf="currentStep() === steps[2].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[2].title }}</h1>
            <p>{{ steps[2].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('typeOfIncident')!"
                [label]="fieldMappings['typeOfIncident']"
                [requiredField]="requiredFields['typeOfIncident']"
                [options]="['Injury/Illness', 'First-Aid', 'Traffic/Auto (Contact Fleet Manager)', 'Property Damage']"
                [orientation]="'vertical'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('dateOfIncident')!"
                [label]="fieldMappings['dateOfIncident']"
                [requiredField]="requiredFields['dateOfIncident']"
            
              >
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('timeOfIncident')!"
                [label]="fieldMappings['timeOfIncident']"
                [type]="'time'"
                [requiredField]="requiredFields['timeOfIncident']"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-date-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('dateReported')!"
                [label]="fieldMappings['dateReported']"
                [requiredField]="requiredFields['dateReported']"
               
              >
              </app-date-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('timeReported')!"
                [label]="fieldMappings['timeReported']"
                [type]="'time'"
                [requiredField]="requiredFields['timeReported']"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('incidentLocation')!"
                [label]="fieldMappings['incidentLocation']"
                [requiredField]="requiredFields['incidentLocation']"
                placeholder="Enter exact location of incident"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('weather')!"
                [label]="fieldMappings['weather']"
                [requiredField]="requiredFields['weather']"
                placeholder="Enter weather conditions"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('temperature')!"
                [label]="fieldMappings['temperature']"
                [requiredField]="requiredFields['temperature']"
                [type]="'number'"
                placeholder="Enter temperature in Fahrenheit"
              >
              </app-text-input>
            </ion-col>
          </ion-row>

          <!-- Equipment Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[2].sections?.[1]?.title }}</h2>
            </ion-label>
          </ion-item-divider>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('equipment1')!"
                [label]="fieldMappings['equipment1']"
                [requiredField]="requiredFields['equipment1']"
                placeholder="Enter primary equipment/tool involved"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('equipment2')!"
                [label]="fieldMappings['equipment2']"
                placeholder="Enter additional equipment/tool (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('anyDefects')!"
                [label]="fieldMappings['anyDefects']"
                placeholder="Describe any defects"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('coveredInPreTask')!"
                [label]="fieldMappings['coveredInPreTask']"
                [requiredField]="requiredFields['coveredInPreTask']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Witnesses Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[2].sections?.[2]?.title }}</h2>
            </ion-label>
          </ion-item-divider>

          <ion-row>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('witness1')!"
                [label]="fieldMappings['witness1']"
                [requiredField]="requiredFields['witness1']"
                placeholder="Enter witness name/contact"
              >
              </app-text-input>
            </ion-col>
            <ion-col size="12" size-md="6">
              <app-text-input
                [templateFormGroup]="incidentDetailsFormGroup"
                [control]="incidentDetailsFormGroup.get('witness2')!"
                [label]="fieldMappings['witness2']"
                placeholder="Enter additional witness (optional)"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Incident Analysis Step -->
    <div *ngIf="currentStep() === steps[3].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[3].title }}</h1>
            <p>{{ steps[3].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('employeeActivity')!"
                [label]="fieldMappings['employeeActivity']"
                [requiredField]="requiredFields['employeeActivity']"
                [rows]="4"
                placeholder="Describe what the employee was doing at the time of the incident"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('contributingFactors')!"
                [label]="fieldMappings['contributingFactors']"
                [requiredField]="requiredFields['contributingFactors']"
                [rows]="3"
                placeholder="Describe contributing factors that led to the incident"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('safetyProcedures')!"
                [label]="fieldMappings['safetyProcedures']"
                [requiredField]="requiredFields['safetyProcedures']"
                [rows]="3"
                placeholder="What safety procedures were involved in the task?"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('employeeFamiliarWithProcedures')!"
                [label]="fieldMappings['employeeFamiliarWithProcedures']"
                [requiredField]="requiredFields['employeeFamiliarWithProcedures']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('employeeFollowedProcedures')!"
                [label]="fieldMappings['employeeFollowedProcedures']"
                [requiredField]="requiredFields['employeeFollowedProcedures']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="incidentAnalysisFormGroup.get('employeeFollowedProcedures')?.value === 'No'">
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('whyNotFollowed')!"
                [label]="fieldMappings['whyNotFollowed']"
                [requiredField]="true"
                [rows]="3"
                placeholder="Explain why procedures were not followed"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>

          <!-- Incident Causes Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[3].sections?.[1]?.title }}</h2>
            </ion-label>
          </ion-item-divider>

          <ion-row>
            <ion-col>
              <app-checkbox-input
                [templateFormGroup]="incidentCausesCheckboxesFormGroup"
                [checkboxes]="incidentCausesCheckboxes"
                [label]="fieldMappings['incidentCausesLabel']"
                [requiredField]="true"
                [columns]="2"
              >
              </app-checkbox-input>
            </ion-col>
          </ion-row>
          <ion-row *ngIf="incidentCausesCheckboxesFormGroup.get('otherCause')?.value">
            <ion-col size="12">
              <app-text-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('otherCauseSpecify')!"
                [label]="fieldMappings['otherCauseSpecify']"
                [requiredField]="true"
                placeholder="Please specify the other cause"
              >
              </app-text-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="incidentAnalysisFormGroup"
                [control]="incidentAnalysisFormGroup.get('incidentCausesDetail')!"
                [label]="fieldMappings['incidentCausesDetail']"
                [requiredField]="requiredFields['incidentCausesDetail']"
                [rows]="4"
                placeholder="Explain in detail all the causes of the incident"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Injury Assessment Step -->
      <div *ngIf="currentStep() === steps[4].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[4].title }}</h1>
            <p>{{ steps[4].description }}</p>
          </ion-label>
        </ion-item-divider>

        <ion-grid>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="injuryAssessmentFormGroup"
                [control]="injuryAssessmentFormGroup.get('injured')!"
                [label]="fieldMappings['injured']"
                [requiredField]="requiredFields['injured']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="injuryAssessmentFormGroup"
                [control]="injuryAssessmentFormGroup.get('bloodExposure')!"
                [label]="fieldMappings['bloodExposure']"
                [requiredField]="requiredFields['bloodExposure']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="injuryAssessmentFormGroup"
                [control]="injuryAssessmentFormGroup.get('seekMedicalTreatment')!"
                [label]="fieldMappings['seekMedicalTreatment']"
                [requiredField]="requiredFields['seekMedicalTreatment']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>

          <!-- Contact Safety Director message (conditional) -->
          <ion-row *ngIf="injuryAssessmentFormGroup.get('seekMedicalTreatment')?.value === 'Yes'">
            <ion-col size="12">
              <ion-card color="warning" class="information-card">
                <ion-card-content>
                  <ion-text >
                    <h5><ion-icon name="alert-circle"></ion-icon>&nbsp;Contact the Safety Director - <strong>206.255.3388</strong></h5>
                  </ion-text>
                </ion-card-content>
              </ion-card>
            </ion-col>
          </ion-row>

          <!-- Medical Treatment Details (conditional) -->
          <div *ngIf="injuryAssessmentFormGroup.get('seekMedicalTreatment')?.value === 'Yes'">
            <ion-item-divider class="form-section-title">
              <ion-label>
                <h2>{{ steps[4].sections?.[1]?.title }}</h2>
              </ion-label>
            </ion-item-divider>

            <ion-row>
              <ion-col size="12">
                <app-text-input
                  [templateFormGroup]="injuryAssessmentFormGroup"
                  [control]="injuryAssessmentFormGroup.get('treatmentFacilityName')!"
                  [label]="fieldMappings['treatmentFacilityName']"
                  [requiredField]="true"
                  placeholder="Enter name of treatment facility"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12" size-md="6">
                <app-text-input
                  [templateFormGroup]="injuryAssessmentFormGroup"
                  [control]="injuryAssessmentFormGroup.get('doctorFirstName')!"
                  [label]="fieldMappings['doctorFirstName']"
                  [requiredField]="true"
                  placeholder="Enter doctor's first name"
                >
                </app-text-input>
              </ion-col>
              <ion-col size="12" size-md="6">
                <app-text-input
                  [templateFormGroup]="injuryAssessmentFormGroup"
                  [control]="injuryAssessmentFormGroup.get('doctorLastName')!"
                  [label]="fieldMappings['doctorLastName']"
                  [requiredField]="true"
                  placeholder="Enter doctor's last name"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
            <ion-row>
              <ion-col size="12">
                <app-text-input
                  [templateFormGroup]="injuryAssessmentFormGroup"
                  [control]="injuryAssessmentFormGroup.get('doctorContactInfo')!"
                  [label]="fieldMappings['doctorContactInfo']"
                  [requiredField]="true"
                  placeholder="Enter doctor contact information"
                >
                </app-text-input>
              </ion-col>
            </ion-row>
          </div>

          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="injuryAssessmentFormGroup"
                [control]="injuryAssessmentFormGroup.get('propertyDamage')!"
                [label]="fieldMappings['propertyDamage']"
                [requiredField]="requiredFields['propertyDamage']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-radio-group-input
                [templateFormGroup]="injuryAssessmentFormGroup"
                [control]="injuryAssessmentFormGroup.get('othersInvolved')!"
                [label]="fieldMappings['othersInvolved']"
                [requiredField]="requiredFields['othersInvolved']"
                [options]="['Yes', 'No']"
                [orientation]="'horizontal'"
              >
              </app-radio-group-input>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-textarea-input
                [templateFormGroup]="injuryAssessmentFormGroup"
                [control]="injuryAssessmentFormGroup.get('preventionSuggestion')!"
                [label]="fieldMappings['preventionSuggestion']"
                [requiredField]="requiredFields['preventionSuggestion']"
                [rows]="4"
                placeholder="How could this incident have been avoided?"
              >
              </app-textarea-input>
            </ion-col>
          </ion-row>

          <!-- Attachments Section -->
          <ion-item-divider class="form-section-title">
            <ion-label>
              <h2>{{ steps[4].sections?.[3]?.title }}</h2>
            </ion-label>
          </ion-item-divider>

          <ion-row>
            <ion-col size="12">
              <app-attachment-upload-section [uploadInfo]="formAttachmentRequirements!.uploads![0]"></app-attachment-upload-section>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>

    <!-- Sign Off Step -->
    <div *ngIf="currentStep() === steps[5].id" class="step-content">
    <ion-list>
      <ion-item-divider class="form-step-title">
          <ion-label>
            <h1>{{ steps[5].title }}</h1>
          </ion-label>
        </ion-item-divider>

        <ion-card color="primary" class="information-card">
          <ion-card-header><ion-card-title><ion-icon name="finger-print-outline"></ion-icon>&nbsp;Declaration</ion-card-title></ion-card-header>
          <ion-card-content>
            <ion-text>
              <h4>The facts as I have stated them are true to the best of my knowledge. 
                <br><br>In signing this form, I permit the release of all information related to this injury to my employer and his/her authorized representative. 
                <br>
                <br>A copy of this permission constitutes as an original.</h4>
            </ion-text>
          </ion-card-content>
        </ion-card>

        <!-- Signature and Date -->
        <ion-grid>
          <ion-row *ngFor="let signature of formAttachmentRequirements?.signatures">
            <ion-col size="12">
              <app-signature-section [signatureInfo]="signature"></app-signature-section>
            </ion-col>
          </ion-row>
          <ion-row>
            <ion-col size="12">
              <app-date-input
                [templateFormGroup]="signOffFormGroup"
                [control]="signOffFormGroup.get('signatureDate')!"
                [label]="fieldMappings['signatureDate']"
                [requiredField]="requiredFields['signatureDate']"
             
              >
              </app-date-input>
            </ion-col>
          </ion-row>
        </ion-grid>
      </ion-list>
    </div>
  </div>
</app-form-layout>